import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import { useEffect, useRef, useState } from 'react';
import { PqrClosing } from '../../../../../../shared/types/modules/pqr';
import { PqrClosingProps } from './props';
import {
    asyncGetParamByCode,
    asyncGetParamById,
    asyncGetParams,
} from '../../../../../../services/param.service';
import { useGetNameOrValue } from '../../../../../../shared/utils/useGetNameOrValue';

const SFC_IF_AUDIT_TYPES = [
    `${ParamEnum.PQR_TYPE}_QEX`,
    `${ParamEnum.PQR_TYPE}_QOR`,
];

export const usePqrClosing = ({
    data,
    onChangeData,
    onChangeFormik,
    isSample,
    status,
    typePqr,
    isClousureStatus,
}: PqrClosingProps) => {
    const [isClose, setIsClose] = useState<boolean>(false);
    const [isQorOrQexType, setIsQorOrQexType] = useState(false);
    const [isSfcSendInfoOpen, setIsSfcSendInfoOpen] = useState(false);
    const [sfcSendInfoWillSend, setSfcSendInfoWillSend] = useState(false);
    const lastNotifiedSendToSfcRef = useRef<boolean | undefined>(undefined);
    const [complianceTerms, setComplianceTerms] = useState<ISelect[]>([]);
    const [nonComplianceReasons, setNonComplianceReasons] = useState<ISelect[]>(
        []
    );
    const [compensation, setTypeCompensation] = useState<ISelect[]>([]);
    const [typeCompensationParam, setTypeCompensations] = useState<ISelect>();

    const onSubmit = (data: PqrClosing) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(isSample),
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue('observation', data?.observation || '');
        formik?.setFieldValue('complianceTerms', data?.complianceTerms || '');
        formik?.setFieldValue(
            'nonComplianceReason',
            data?.nonComplianceReason || ''
        );
        formik?.setFieldValue('compensation', data?.compensation || false);
        formik?.setFieldValue(
            'auditObservations',
            data?.auditObservations || ''
        );
        formik?.setFieldValue(
            'audit',
            !data?.audit ? isSample : data?.audit || false
        );
        formik?.setFieldValue(
            'emailNotification',
            data?.emailNotification || false
        );
        formik?.setFieldValue(
            'manualSendToSfcIfAudit',
            data?.manualSendToSfcIfAudit ?? true
        );
        formik?.setFieldValue(
            'typeCompensation',
            useGetNameOrValue(data?.typeCompensation || '')
        );
    }, [data, isSample]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_CLOSING_COMPLIANCE_TERMS).then((items) =>
            setComplianceTerms(items)
        );
        asyncGetParams(ParamEnum.PQR_CLOSING_NON_COMPLIANCE_REASON).then(
            (items) => setNonComplianceReasons(items)
        );
    }, []);

    useEffect(() => {
        if (status) {
            asyncGetParamById(status).then((items) =>
                setIsClose(items?.code === 'PQR_STATUS_CER')
            );
        }
    }, [status]);

    useEffect(() => {
        if (!typePqr) {
            setIsQorOrQexType(false);
            return;
        }
        asyncGetParamById(typePqr).then((item) => {
            setIsQorOrQexType(
                Boolean(item?.code && SFC_IF_AUDIT_TYPES.includes(item.code))
            );
        });
    }, [typePqr]);

    // TODO: reactivar modal "Enviar a la SFC" cuando se requiera en producción
    /*
    const openSfcSendInfoModal = (willSend: boolean) => {
        setSfcSendInfoWillSend(willSend);
        setIsSfcSendInfoOpen(true);
    };

    useEffect(() => {
        const isClosureWithQorQex = Boolean(isClousureStatus) && isQorOrQexType;

        if (!isClosureWithQorQex) {
            setIsSfcSendInfoOpen(false);
            lastNotifiedSendToSfcRef.current = undefined;
            return;
        }

        const sendToSfc = Boolean(formik.values.manualSendToSfcIfAudit);
        const previous = lastNotifiedSendToSfcRef.current;

        if (previous === undefined) {
            lastNotifiedSendToSfcRef.current = sendToSfc;
            openSfcSendInfoModal(sendToSfc);
            return;
        }

        if (previous !== sendToSfc) {
            lastNotifiedSendToSfcRef.current = sendToSfc;
            openSfcSendInfoModal(sendToSfc);
        }
    }, [
        isClousureStatus,
        isQorOrQexType,
        formik.values.manualSendToSfcIfAudit,
    ]);
    */
    useEffect(() => {
        asyncGetParams(ParamEnum.TYPE_COMPENSATION).then((items) =>
            setTypeCompensation(items)
        );
        asyncGetParamByCode(`${ParamEnum.TYPE_COMPENSATION}`).then((item) =>
            setTypeCompensations(item)
        );
    }, []);

    useEffect(() => {
        formik?.setFieldValue('pqrTypeCompensation', '');
        formik?.setFieldValue('observations', '');
    }, [formik.values.typeCompensation]);

    const handleCloseSfcSendInfo = () => setIsSfcSendInfoOpen(false);

    return {
        formik,
        complianceTerms,
        nonComplianceReasons,
        isClose,
        compensation,
        typeCompensationParam,
        isQorOrQexType,
        isSfcSendInfoOpen,
        sfcSendInfoWillSend,
        handleCloseSfcSendInfo,
    };
};
